(() => {
  "use strict";

  const GLOBAL_KEY = "__glydeDepositSubscribe";
  if (window[GLOBAL_KEY]?.init) {
    window[GLOBAL_KEY].init(document);
    return;
  }

  const FORM_SELECTOR = "form[data-glyde-deposit-subscribe]";
  const DEFAULT_ERROR = "We couldn’t save your email. Please try again.";
  const ENDPOINT = "https://glydeclipper.online/api/subscribe";
  const formTeardowns = new WeakMap();
  const activeForms = new Set();

  function initializeForm(form) {
    if (formTeardowns.has(form)) return;

    const section = form.closest("[data-glyde-deposit-subscribe-section]");
    const email = form.querySelector("[data-subscribe-email]");
    const honeypot = form.querySelector("[data-subscribe-honeypot]");
    const button = form.querySelector("[data-subscribe-button]");
    const success = form.querySelector("[data-subscribe-success]");
    const error = section?.querySelector("[data-subscribe-error]");
    if (
      !(section instanceof HTMLElement) ||
      !(email instanceof HTMLInputElement) ||
      !(button instanceof HTMLButtonElement) ||
      !(success instanceof HTMLElement) ||
      !(error instanceof HTMLElement)
    ) return;

    let busy = false;
    let destroyed = false;
    let request = null;
    let countdown = 0;
    let requestTimeout = 0;

    function setState(state, message = "") {
      const successful = state === "success";
      const disabled = state === "submitting" || successful;
      form.dataset.state = state;
      section.dataset.state = state;
      form.setAttribute("aria-busy", String(state === "submitting"));
      email.disabled = disabled;
      button.disabled = disabled;
      email.setAttribute("aria-hidden", String(successful));
      button.setAttribute("aria-hidden", String(successful));
      success.setAttribute("aria-hidden", String(!successful));
      button.textContent = state === "submitting" ? "Submitting…" : "Remind me";
      error.textContent = message;

      if (state === "error") {
        email.setAttribute("aria-invalid", "true");
        email.setAttribute("aria-describedby", error.id);
      } else {
        email.removeAttribute("aria-invalid");
        email.removeAttribute("aria-describedby");
      }
    }

    function showSuccess() {
      const resetAt = Date.now() + 3_000;
      success.textContent = "Submission successful（3s）";
      setState("success");

      countdown = window.setInterval(() => {
        const remaining = Math.max(0, Math.ceil((resetAt - Date.now()) / 1_000));
        if (remaining > 0) {
          success.textContent = `Submission successful（${remaining}s）`;
          return;
        }

        window.clearInterval(countdown);
        countdown = 0;
        form.reset();
        busy = false;
        success.textContent = "";
        setState("idle");
      }, 100);
    }

    async function handleSubmit(event) {
      event.preventDefault();
      if (busy || !form.reportValidity()) return;

      busy = true;
      const controller = new AbortController();
      request = controller;
      requestTimeout = window.setTimeout(() => controller.abort(), 15_000);
      setState("submitting");

      try {
        const response = await fetch(ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            email: email.value.trim(),
            website: honeypot instanceof HTMLInputElement ? honeypot.value : "",
            source: "deposit-updates",
          }),
          credentials: "omit",
          cache: "no-store",
          signal: controller.signal,
        });
        const result = await response.json();
        if (!response.ok || !result || result.ok !== true) throw new Error(DEFAULT_ERROR);
        if (!destroyed) showSuccess();
      } catch {
        if (destroyed) return;
        busy = false;
        setState("error", controller.signal.aborted ? "The request timed out. Please try again." : DEFAULT_ERROR);
      } finally {
        window.clearTimeout(requestTimeout);
        requestTimeout = 0;
        if (request === controller) request = null;
      }
    }

    const teardown = () => {
      destroyed = true;
      request?.abort();
      window.clearInterval(countdown);
      window.clearTimeout(requestTimeout);
      form.removeEventListener("submit", handleSubmit);
      activeForms.delete(form);
      formTeardowns.delete(form);
    };

    form.addEventListener("submit", handleSubmit);
    formTeardowns.set(form, teardown);
    activeForms.add(form);
    setState("idle");
  }

  function initializeWithin(container = document) {
    if (!container || typeof container.querySelectorAll !== "function") return;
    if (container instanceof HTMLFormElement && container.matches(FORM_SELECTOR)) initializeForm(container);
    container.querySelectorAll(FORM_SELECTOR).forEach(initializeForm);
  }

  function destroyWithin(container) {
    if (!(container instanceof Node)) return;
    for (const form of Array.from(activeForms)) {
      if (form === container || container.contains(form)) formTeardowns.get(form)?.();
    }
  }

  window[GLOBAL_KEY] = { init: initializeWithin, destroy: destroyWithin };
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initializeWithin(), { once: true });
  } else {
    initializeWithin();
  }
  document.addEventListener("shopify:section:load", (event) => initializeWithin(event.target));
  document.addEventListener("shopify:section:unload", (event) => destroyWithin(event.target));
  window.addEventListener("pagehide", () => destroyWithin(document));
  window.addEventListener("pageshow", () => initializeWithin(document));
})();
